<template>
  <div class="d-flex flex-column full-height sidebar-chat">
    <ChatSkeletons v-if="loading" />
    <ChatMessages v-else :messages="messages" :user="user" class="flex-grow-1" />

    <div class="chat-footer flex-shrink-0">
      <ChatAutocomplete
        :open="autocomplete.open"
        :list="autocomplete.list"
        :selected.sync="autocomplete.selected"
        @input="onAutocompleteSelect"
      />

      <ChatInput
        v-if="canChat"
        ref="chatInput"
        :input.sync="input"
        :is-sending="isSending"
        :autocomplete="autocomplete"
        @send="onChatSend"
        @autocomplete-action="onAutocompleteAction"
      />

      <div v-else class="px-3 py-2 text-body-2 text--secondary">
        <div v-if="!user" class="grey500 pa-3">
          <fai :icon="['fab', 'steam-symbol']" class="primary500--text mr-1" />
          <span v-t="'layout.chat.login'" />
        </div>
        <div v-else class="grey500 pa-3">
          <fai :icon="['fad', 'exclamation-triangle']" class="primary500--text mr-1" />
          <span v-text="$tc('layout.chat.level', minLevel)" />
        </div>
      </div>

      <div class="d-flex align-center px-4 mb-2">
        <v-menu offset-y top nudge-top="8" max-width="320">
          <template #activator="{ on }">
            <a class="link grey200--text text-body-2 mb-n1" v-on="on" v-text="$t('cta.rules')" />
          </template>
          <v-card color="grey500" class="pa-4">
            <h5 v-t="'layout.chat.rules'" class="text-body-2 green400--text font-weight-bold mb-1 text-uppercase" />
            <ol class="text-body-2 green100--text mb-2">
              <li v-t="'layout.chat.rules.respect'" />
              <li v-t="'layout.chat.rules.swearing'" />
              <li v-t="'layout.chat.rules.racism'" />
              <li v-t="'layout.chat.rules.spam'" />
              <li v-t="'layout.chat.rules.begging'" />
              <li v-t="'layout.chat.rules.sense'" />
              <li v-t="'layout.chat.rules.rigged'" />
              <li v-t="'layout.chat.rules.codes'" />
              <li v-t="'layout.chat.rules.staff'" />
            </ol>

            <p v-t="'layout.chat.rules.warning'" class="text-body-2 mb-0 primary200--text" />
          </v-card>
        </v-menu>

        <v-tooltip v-if="user && user.hasPermission('CAN_MODIFY_CHAT')" top color="primary700">
          <template #activator="{ on }">
            <fai
              :icon="['fad', 'comment-slash']"
              class="primary300--text text-caption link ml-3"
              v-on="on" @click="onClearRoom()"
            />
          </template>
          <span>Clear room</span>
        </v-tooltip>


        <v-spacer />

        <div class="tilted d-flex align-center">
          <ChatRoomSwitcher :rooms="rooms" :room="room" class="mr-2" @change="onChangeRoom" />

          <v-btn
            class="rounded-0 font-weight-bold"
            color="blue700 blue500--text"
            :ripple="false"
            :loading="isSending"
            @click="onChatSend()"
            v-text="$t('cta.send')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import sortBy from 'lodash.sortby'

import ChatAutocomplete from '@/components/Chat/ChatAutocomplete'
import ChatSkeletons from '@/components/Chat/ChatSkeletons'
import ChatMessages from '@/components/Chat/ChatMessages'
import ChatInput from '@/components/Chat/ChatInput'
import ChatRoomSwitcher from '@/components/Chat/ChatRoomSwitcher'
import {
  emoteList, getAutocompleteType, replaceEmote,
  replaceMention, toEmote, toMention,
} from '@/utils/chat'
import {
  AC_MAX_SUGGESTIONS, AC_TYPE_EMOTE, AC_TYPE_MENTION,
  PING_EVERYONE, PING_MODS,
} from '@/utils/constants/chat'

export default {
  components: {
    ChatRoomSwitcher,
    ChatSkeletons,
    ChatInput,
    ChatMessages,
    ChatAutocomplete,
  },
  data: () => ({
    refetchHistory: false,
    autocomplete: {
      open: false,
      selected: 0,
      list: [],
    },
  }),
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      loading: 'chat/loading',
      room: 'chat/room',
      messages: 'chat/messages',
      isSending: 'chat/isSending',
      subscribed: 'chat/subscribed',
      user: 'user/session',
      connected: 'connected',
    }),
    minLevel() {
      return this.conga.chat?.minLevel ?? 0
    },
    canChat() {
      if (!this.user) return false
      if (this.user.level < this.minLevel) return false

      return true
    },
    chatUsers() {
      const users = {}

      for (const msg of this.messages) {
        const steamid = msg.user?.steamid

        if (steamid) {
          users[steamid] = {
            ...msg.user,
            friendId: msg.friendId,
          }
        }
      }

      return users
    },
    input: {
      get() {
        return this.$store.getters['chat/input']
      },
      set(v) {
        this.setInput(v)
      },
    },
    rooms() {
      return this.conga.chat?.rooms ?? []
    },
  },
  watch: {
    input(input) {
      this.onInputChange(input)
    },
    'autocomplete.list': function onListChange(val) {
      this.autocomplete.selected = 0
    },
    connected: function onConnected(val) {
      if (!val) {
        this.refetchHistory = true
      }
    },
    subscribed: function onSubscribed(val) {
      if (!val) return
      if (!this.refetchHistory) return

      this.refetchHistory = false
      this.getHistory(this.room)
    },
  },
  async created() {
    if (!this.subscribed) {
      await this.subscribe('chat')

      // load last joined chat room, fallback to locale
      const room = this.$ls.get('chatroom', `lang-${this.$i18n.locale}`)

      if (this.rooms.includes(room)) {
        this.onChangeRoom(room)
      } else {
        this.onChangeRoom(this.rooms[0])
      }
    }
  },
  methods: {
    ...mapMutations({
      setInput: 'chat/setInput',
      setRoom: 'chat/setRoom',
    }),
    ...mapActions({
      send: 'chat/send',
      getHistory: 'chat/getHistory',
      subscribe: 'subscribe',
    }),
    async onChatSend(text = this.input) {
      if (this.isSending) return

      try {
        await this.send(text)
      } catch (error) {
        this.$toast.error(error.message)
      }
    },
    onAutocompleteSetSelected(index) {
      this.autocomplete.selected = index
    },
    onAutocompleteSelect(index = this.autocomplete.selected) {
      this.autocomplete.open = false

      const { type, value } = this.autocomplete.list[index]

      switch (type) {
        case AC_TYPE_EMOTE:
          this.setInput(replaceEmote(this.input, value))
          break

        case AC_TYPE_MENTION:
          this.setInput(replaceMention(this.input, value))
          break

        default:
          break
      }

      this.focusInput()
    },
    focusInput() {
      const { $el: $chatInput } = this.$refs.chatInput
      if (!$chatInput) return

      $chatInput.querySelector('textarea').focus()
    },
    onAutocompleteAction(action) {
      const { list } = this.autocomplete

      switch (action) {
        case 'select':
          this.onAutocompleteSelect()
          break

        case 'up':
          if (this.autocomplete.selected === 0) {
            this.autocomplete.selected = list.length - 1
          } else {
            this.autocomplete.selected -= 1
          }
          break

        case 'down':
          if (this.autocomplete.selected === list.length - 1) {
            this.autocomplete.selected = 0
          } else {
            this.autocomplete.selected += 1
          }
          break

        default:
          break
      }
    },
    onInputChange(input) {
      if (input.length > 1) {
        const autocompleteType = getAutocompleteType(input)
        if (!autocompleteType) {
          this.resetAutocomplete()
          return
        }

        this.onAutocompleteInput(autocompleteType, input)
      } else if (this.autocomplete.open) {
        this.resetAutocomplete()
      }
    },
    onAutocompleteInput(type, input) {
      let suggestions = []

      switch (type) {
        case AC_TYPE_MENTION: {
          const mention = toMention(input) ?? ''

          const { chatUsers } = this
          const options = [...Object.entries(chatUsers), [PING_EVERYONE], [PING_MODS]]

          suggestions = sortBy(
            options
              .filter(([id, userInfo]) => (userInfo?.friendId ?? id)
                .toLowerCase().includes(mention.substr(1).toLowerCase()))
              .map(([id, userInfo]) => ({
                value: userInfo?.friendId ?? id,
                type: AC_TYPE_MENTION,
                meta: userInfo ?? null,
              })),
            i => i.value.length,
          )
          break
        }

        case AC_TYPE_EMOTE: {
          const emote = toEmote(input) ?? ''
          const options = emoteList

          suggestions = sortBy(
            options
              .filter(i => i.toLowerCase().includes(emote.substr(1).toLowerCase()))
              .map(i => ({
                value: i,
                type: AC_TYPE_EMOTE,
                meta: null,
              })),
            i => i.value.length,
          )
          break
        }

        default:
          break
      }

      if (suggestions.length > AC_MAX_SUGGESTIONS) {
        suggestions.length = AC_MAX_SUGGESTIONS
      }

      this.autocomplete.list = suggestions
      this.autocomplete.open = !!suggestions.length
    },
    resetAutocomplete() {
      this.autocomplete.open = false
      this.autocomplete.list = []
    },
    async onClearRoom(room = this.room) {
      if (!this.user.hasPermission('CAN_MODIFY_CHAT')) {
        return
      }

      try {
        await this.$socket.request('admin.chat.clear', room)
        this.$toast.info('Room cleared.')
      } catch (error) {
        this.$toast.error(error.message)
      }
    },
    async onChangeRoom(room) {
      this.setRoom(room)
      this.$ls.set('chatroom', room)

      try {
        this.getHistory(room)
      } catch (error) {
        this.$toast.error(error.message, { title: 'Chat' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sidebar-chat {
  &::after {
    position: absolute;
    z-index: 3;
    top: 0;
    left: 0;
    width: 100%;
    height: 150px;
    background: linear-gradient(to top, rgba(grey(800), 0), grey(900));
    content: ' ';
    pointer-events: none;
  }
}

.chat-footer {
  position: relative;

  .chat-autocomplete {
    position: absolute;
    z-index: 3;
    top: -8px;
    left: 0;
    width: 100%;
    transform: translateY(-100%);
  }

  .tilted {
    transform: rotate(-3deg) translate(-4px, -12px);
  }
}
</style>
